/**
 * CWA platform adapter for canonical log lines.
 *
 * Registers the CWA emitter with the shared accumulator once at module load.
 * Emission is gated on the `consumer_canonical_log_lines` Statsig feature gate,
 * read from the already-initialized client stored in analytics-v2.
 *
 * Destinations:
 *   - New Relic Browser Agent: addPageAction with top-14 priority-ordered fields
 *   - GA + Clarity: analytics.track with all fields via existing analytics-v2 instance
 */
import { STATSIG_FEATURE_GATES, registerCanonicalEmitter, type CanonicalFields } from '@consumer/shared'

import { analytics, getStatsigClient } from './analytics-v2'

// ---------------------------------------------------------------------------
// NR Browser Agent reserved attributes — rename to avoid NRDB collisions
// ---------------------------------------------------------------------------

// NOTE: `platform` is intentionally NOT in this list for CWA. The NR Browser Agent
// does not set a `platform` session attribute, so our custom `platform: 'web'` field
// passes through unchanged. This differs from CMA where the NR mobile SDK reserves
// `platform` (set automatically to 'iOS'/'Android'), so CMA renames it to `platformVeho`.
// NR Browser Agent addPageAction queries should use `platform`, CMA queries use `platformVeho`.
const RESERVED_ATTRIBUTES = [
  'accountId',
  'appId',
  'currentUrl',
  'eventType',
  'pageUrl',
  'referrerUrl',
  'sessionId',
  'timestamp',
  'type',
] as const

// ---------------------------------------------------------------------------
// Field priority tables (NR addPageAction is capped at 64 kB but we self-cap
// at 14 for parity with CMA and to control NRDB index cardinality)
// ---------------------------------------------------------------------------

const AUTH_FLOW_PRIORITY: readonly string[] = [
  'outcome',
  'authFlow',
  'totalDurationMs',
  'otpRequestDurationMs',
  'verifyDurationMs',
  'errorStage',
  'attemptCount',
  'platform',
  'error',
  'emittedAt',
]

const INSTRUCTIONS_SAVE_PRIORITY: readonly string[] = [
  'outcome',
  'totalDurationMs',
  'mutationDurationMs',
  'errorStage',
  'propertyType',
  'hasAddressInstructions',
  'hasPlacementPhoto',
  'platform',
  'error',
  'emittedAt',
]

const TRACKING_LOAD_PRIORITY: readonly string[] = [
  'outcome',
  'queryDurationMs',
  'packageCount',
  'orderStatus',
  'isAuthenticated',
  'platform',
  'errorType',
  'emittedAt',
]

const ADDRESS_UPDATE_PRIORITY: readonly string[] = [
  'outcome',
  'totalDurationMs',
  'mutationDurationMs',
  'errorStage',
  'platform',
  'error',
  'emittedAt',
]

const PRIORITY_BY_EVENT_TYPE: Record<string, readonly string[]> = {
  CanonicalAuthFlow: AUTH_FLOW_PRIORITY,
  CanonicalInstructionsSave: INSTRUCTIONS_SAVE_PRIORITY,
  CanonicalTrackingLoad: TRACKING_LOAD_PRIORITY,
  CanonicalAddressUpdate: ADDRESS_UPDATE_PRIORITY,
}

const NR_MAX_ATTRIBUTES = 14

const pickTopFields = (fields: CanonicalFields, eventType: string): CanonicalFields => {
  const priority = PRIORITY_BY_EVENT_TYPE[eventType]
  if (!priority) {
    const entries = Object.entries(fields)
    return entries.length <= NR_MAX_ATTRIBUTES ? fields : Object.fromEntries(entries.slice(0, NR_MAX_ATTRIBUTES))
  }

  const result: CanonicalFields = {}
  let count = 0

  for (const key of priority) {
    if (count >= NR_MAX_ATTRIBUTES) break
    if (key in fields) {
      result[key] = fields[key]
      count++
    }
  }

  for (const [key, value] of Object.entries(fields)) {
    if (count >= NR_MAX_ATTRIBUTES) break
    if (!(key in result)) {
      result[key] = value
      count++
    }
  }

  return result
}

// ---------------------------------------------------------------------------
// Register CWA emitter
// ---------------------------------------------------------------------------

registerCanonicalEmitter({
  isEnabled: () => getStatsigClient()?.checkGate(STATSIG_FEATURE_GATES.consumer_canonical_log_lines) ?? false,

  emit: (eventType: string, fields: CanonicalFields) => {
    // New Relic Browser Agent — top-14 priority fields, reserved attributes renamed
    try {
      const nrFields = pickTopFields(fields, eventType)
      const sanitized: CanonicalFields = { ...nrFields }
      for (const attr of RESERVED_ATTRIBUTES) {
        if (attr in sanitized) {
          sanitized[`${attr}Veho`] = sanitized[attr]
          delete sanitized[attr]
        }
      }
      window.newrelic?.addPageAction(eventType, sanitized)
    } catch {
      // NR emission failure must not prevent analytics or crash the app
    }

    // GA + Clarity via existing analytics instance — all fields.
    // analytics.track returns a Promise; consume the rejection explicitly so
    // an async plugin failure does not become an unhandled promise rejection.
    try {
      Promise.resolve(analytics.track(eventType, fields)).catch(() => undefined)
    } catch {
      // Analytics emission failure must not crash the app
    }
  },
})
